<template>
  <transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-2 scale-95"
    enter-to-class="opacity-100 translate-y-0 scale-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0 scale-100"
    leave-to-class="opacity-0 translate-y-2 scale-95"
  >
    <div
      v-if="visible"
      class="absolute right-0 mt-2 w-[380px] max-h-[480px] bg-premium-card border border-premium-border rounded-2xl shadow-2xl backdrop-blur-xl overflow-hidden z-50 flex flex-col"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-5 py-4 border-b border-premium-border">
        <h3 class="text-sm font-bold text-premium-text uppercase tracking-widest">
          Notifications
        </h3>
        <button
          v-if="notifications.length > 0 && hasUnread"
          @click="markAllRead"
          class="text-[10px] font-bold text-brand-400 hover:text-brand-300 uppercase tracking-widest transition-colors"
        >
          Mark all read
        </button>
      </div>

      <!-- Notification List -->
      <div class="flex-1 overflow-y-auto" ref="listContainer">
        <!-- Loading State -->
        <div v-if="isLoading && notifications.length === 0" class="p-6">
          <div v-for="i in 3" :key="i" class="flex gap-3 mb-4 animate-pulse">
            <div class="w-9 h-9 rounded-xl bg-white/5 flex-shrink-0"></div>
            <div class="flex-1">
              <div class="h-3 bg-white/5 rounded w-3/4 mb-2"></div>
              <div class="h-2.5 bg-white/5 rounded w-full"></div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="notifications.length === 0"
          class="flex flex-col items-center justify-center py-12 px-6"
        >
          <div class="w-14 h-14 bg-white/5 rounded-2xl flex items-center justify-center mb-4">
            <BellOff class="w-7 h-7 text-premium-dim" />
          </div>
          <p class="text-sm font-bold text-premium-text mb-1">No notifications yet</p>
          <p class="text-xs text-premium-dim text-center">
            You'll see signal digests, campaign updates, and system alerts here.
          </p>
        </div>

        <!-- Notification Items -->
        <div v-else>
          <div
            v-for="notification in notifications"
            :key="notification.id"
            @click="handleClick(notification)"
            class="flex gap-3 px-5 py-4 cursor-pointer transition-colors border-b border-premium-border/50 last:border-0"
            :class="notification.read ? 'hover:bg-white/3' : 'bg-white/[0.02] hover:bg-white/5'"
          >
            <!-- Icon -->
            <div
              class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              :class="getIconClasses(notification.type)"
            >
              <component :is="getIcon(notification.type)" class="w-4.5 h-4.5" />
            </div>

            <!-- Content -->
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-2">
                <p
                  class="text-sm leading-tight line-clamp-1"
                  :class="notification.read ? 'text-premium-dim font-medium' : 'text-premium-text font-bold'"
                >
                  {{ notification.title }}
                </p>
                <!-- Unread dot -->
                <span
                  v-if="!notification.read"
                  class="w-2 h-2 rounded-full bg-brand-500 flex-shrink-0 mt-1.5"
                ></span>
              </div>
              <p
                v-if="notification.message"
                class="text-xs text-premium-dim mt-0.5 line-clamp-2 leading-relaxed"
              >
                {{ notification.message }}
              </p>
              <span class="text-[10px] text-premium-dim/60 mt-1 block uppercase tracking-widest font-bold">
                {{ formatTime(notification.createdAt) }}
              </span>
            </div>
          </div>

          <!-- Load More -->
          <div v-if="hasMore" class="px-5 py-3 border-t border-premium-border/50">
            <button
              @click="loadMore"
              :disabled="isLoadingMore"
              class="w-full py-2.5 text-xs font-bold text-brand-400 hover:text-brand-300 uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
            >
              <Loader2 v-if="isLoadingMore" class="w-3.5 h-3.5 animate-spin" />
              <template v-else>
                <ChevronDown class="w-3.5 h-3.5" />
                Load More
              </template>
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  BarChart3,
  Target,
  Settings,
  Flame,
  BellOff,
  Loader2,
  ChevronDown,
} from "lucide-vue-next";

export default {
  name: "NotificationPanel",
  components: { BarChart3, Target, Settings, Flame, BellOff, Loader2, ChevronDown },
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      isLoading: false,
      isLoadingMore: false,
      currentPage: 1,
      hasMore: false,
    };
  },
  computed: {
    notifications() {
      return this.$store.state.notifications;
    },
    hasUnread() {
      return this.$store.state.unreadNotificationCount > 0;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.loadNotifications();
      }
    },
  },
  methods: {
    async loadNotifications() {
      this.isLoading = true;
      this.currentPage = 1;
      try {
        const pagination = await this.$store.dispatch("fetchNotifications", { page: 1 });
        this.hasMore = pagination?.hasMore || false;
      } catch (err) {
        console.error("Failed to load notifications", err);
      } finally {
        this.isLoading = false;
      }
    },

    async loadMore() {
      this.isLoadingMore = true;
      this.currentPage++;
      try {
        const pagination = await this.$store.dispatch("fetchNotifications", {
          page: this.currentPage,
          append: true,
        });
        this.hasMore = pagination?.hasMore || false;
      } catch (err) {
        this.currentPage--;
      } finally {
        this.isLoadingMore = false;
      }
    },

    async markAllRead() {
      await this.$store.dispatch("markAllNotificationsRead");
    },

    async handleClick(notification) {
      if (!notification.read) {
        await this.$store.dispatch("markNotificationRead", notification.id);
      }
      const link = notification.metadata?.link;
      if (link) {
        this.$emit("close");
        this.$router.push(link);
      }
    },

    getIcon(type) {
      const map = {
        signal_digest: "BarChart3",
        campaign_update: "Target",
        system: "Settings",
        new_signals: "Flame",
      };
      return map[type] || "Settings";
    },

    getIconClasses(type) {
      const map = {
        signal_digest: "bg-blue-500/10 text-blue-400",
        campaign_update: "bg-emerald-500/10 text-emerald-400",
        system: "bg-purple-500/10 text-purple-400",
        new_signals: "bg-orange-500/10 text-orange-400",
      };
      return map[type] || "bg-white/5 text-premium-dim";
    },

    formatTime(dateStr) {
      if (!dateStr) return "";
      const now = new Date();
      const date = new Date(dateStr);
      const diffMs = now - date;
      const diffMin = Math.floor(diffMs / 60000);
      const diffHr = Math.floor(diffMs / 3600000);
      const diffDay = Math.floor(diffMs / 86400000);

      if (diffMin < 1) return "Just now";
      if (diffMin < 60) return `${diffMin}m ago`;
      if (diffHr < 24) return `${diffHr}h ago`;
      if (diffDay === 1) return "Yesterday";
      if (diffDay < 7) return `${diffDay}d ago`;
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    },
  },
};
</script>
